import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/siteData';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = () => {
    setIsDropdownOpen(false);
    setIsMobileCatOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          <Link to="/" className="flex items-center gap-2 no-underline">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-grid-fill text-white text-lg"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">
              Vigorbuy Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">Home</Link>
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleCategoryClick}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <Link to="/blog/vigorbuy-spreadsheet-complete-guide" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">Guides</Link>
            <Link to="/blog/vigorbuy-spreadsheet-how-to-use" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">Shopping Guide</Link>
            <Link to="/blog/vigorbuy-spreadsheet-safety-guide" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">Safety</Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 py-4 pb-6 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-4-line text-base"></i>
                </div>
                Home
              </Link>

              <div>
                <button
                  onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-apps-2-line text-base"></i>
                    </div>
                    Categories
                  </div>
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isMobileCatOpen && (
                  <div className="ml-4 mt-1 grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={handleCategoryClick}
                        className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
                      >
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className={`${cat.icon} text-sm`}></i>
                        </div>
                        <span className="truncate">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/blog/vigorbuy-spreadsheet-complete-guide" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-book-open-line text-base"></i>
                </div>
                Guides
              </Link>

              <Link to="/blog/vigorbuy-spreadsheet-how-to-use" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line text-base"></i>
                </div>
                Shopping Guide
              </Link>

              <Link to="/blog/vigorbuy-spreadsheet-safety-guide" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shield-check-line text-base"></i>
                </div>
                Safety
              </Link>

              <div className="mx-4 mt-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap text-center cursor-pointer"
                >
                  Explore Now
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}